import { PA_COURSE_DURATIONS, PA_OPTIONS } from '@/constants/paStudentsRateCard';
import { computePaCoverEndDate } from '@/utils/paCoverDates';
import {
  createPdfLayout,
  formatPdfCurrency,
  formatPdfDate,
  renderBenefitsTable,
  renderCompanyHeader,
  renderDocumentTitle,
  renderGrandTotal,
  renderInfoBox,
  renderIntermediaryStrip,
  renderInvoiceMeta,
  renderTableHeader,
  renderTableRow,
  renderValidityAndFooter,
} from '@/utils/quotePdfTheme';

function resolveDurationLabel(quote) {
  const duration = PA_COURSE_DURATIONS.find((d) => d.code === quote.courseDuration);
  return duration?.label || quote.courseDuration || 'N/A';
}

export function resolvePaBenefits(quote) {
  if (Array.isArray(quote.benefits) && quote.benefits.length) {
    return quote.benefits;
  }
  const opt = PA_OPTIONS.find(
    (o) => o.code === quote.optionCode || o.name === quote.optionName,
  );
  return opt?.benefits || [];
}

function resolveCoverPeriod(quote) {
  const from = quote.coverFrom || '';
  const to = quote.coverTo
    || (from && quote.courseDuration ? computePaCoverEndDate(from, quote.courseDuration) : '');
  if (from && to) return `${from} to ${to}`;
  if (from) return `From ${from}`;
  return 'Upon payment';
}

/**
 * Build motor-style Students PA quote PDF (jsPDF).
 */
export function buildPaQuotePDF(doc, quote, options = {}) {
  const clientName = options.clientName || quote.proposerName || 'N/A';
  const clientEmail = options.clientEmail || quote.email || quote.proposerEmail || 'N/A';
  const agentName = options.agentName || 'N/A';
  const quoteId = quote.quoteId || '—';
  const invoiceNo = `INV-${String(quoteId).replace(/\D/g, '').padStart(6, '0').slice(-6) || quoteId}`;
  const durationLabel = resolveDurationLabel(quote);
  const benefits = resolvePaBenefits(quote);
  const premium = Number(quote.premium || 0);

  const layout = createPdfLayout(doc);
  let y = renderCompanyHeader(doc, layout, { agentName });
  y = renderDocumentTitle(doc, layout, 'STUDENTS PERSONAL ACCIDENT INVOICE', y);
  y = renderInvoiceMeta(doc, layout, { invoiceNo, clientName, clientEmail }, y);

  const boxH = Math.max(30, 11 + Math.max(4, quote.nationalId ? 4 : 3) * 6 + 4);
  const colW = (layout.contentW - 6) / 2;
  const leftRows = [
    ['Name', clientName],
    ['Email', clientEmail],
    ['Quote ID', String(quoteId)],
  ];
  const rightRows = [
    ['Plan', quote.optionName || 'N/A'],
    ['Duration', durationLabel],
    ['Cover period', resolveCoverPeriod(quote)],
    ...(quote.nationalId ? [['National ID', quote.nationalId]] : []),
  ];
  renderInfoBox(doc, layout.margin, y, colW, boxH, 'CLIENT DETAILS', leftRows);
  renderInfoBox(doc, layout.margin + colW + 6, y, colW, boxH, 'COVER DETAILS', rightRows);
  y += boxH + 6;

  y = renderIntermediaryStrip(doc, layout, agentName, y);
  y = renderTableHeader(doc, layout, 'INVOICE SUMMARY', 'Amount (Ksh)', y);
  y = renderTableRow(
    doc,
    layout,
    `Students PA — ${quote.optionName || 'Personal Accident'}`,
    premium,
    y,
  );
  y = renderGrandTotal(doc, layout, premium, y);
  y = renderBenefitsTable(doc, layout, benefits, y);
  renderValidityAndFooter(doc, layout, y);
}

export function buildPaQuoteEmailHtml(quote, customerName, options = {}) {
  const name = customerName || quote.proposerName || 'Customer';
  const agentName = options.agentName || 'Pacis Insurance';
  const quoteId = quote.quoteId || '—';
  const invoiceNo = `INV-${String(quoteId).replace(/\D/g, '').padStart(6, '0').slice(-6) || quoteId}`;
  const durationLabel = resolveDurationLabel(quote);
  const benefits = resolvePaBenefits(quote);
  const premium = Number(quote.premium || 0);
  const dateStr = formatPdfDate();

  const benefitRows = benefits
    .map((b, i) => {
      const bg = i % 2 === 0 ? 'background:#FAFCFF;' : '';
      return `<tr style="border-bottom:1px solid #f1f5f9;${bg}">
        <td style="padding:6px 10px;font-size:11px;font-weight:600;color:#1E4276;">${b.label}</td>
        <td style="padding:6px 10px;font-size:11px;text-align:right;font-weight:700;color:#00AEEF;">KES ${formatPdfCurrency(b.limit)}</td>
      </tr>`;
    })
    .join('');

  return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;padding:24px 0;"><tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(30,66,118,0.08);">
<tr><td style="padding:16px 20px;border-bottom:1px solid #e2e8f0;">
  <table width="100%"><tr>
    <td><div style="font-size:16px;font-weight:800;color:#1E4276;">Pacis Insurance</div>
    <div style="font-size:11px;color:#64748b;margin-top:2px;">With you every step of the way</div></td>
    <td style="text-align:right;font-size:9px;color:#64748b;line-height:1.5;">
      <div style="font-weight:700;color:#1E4276;">Pacis Insurance Company Limited</div>
      Pacis Centre, 4th Floor, off Waiyaki Way<br>P.O BOX 1870-00200, Nairobi<br>
      <span style="color:#00AEEF;font-weight:700;">INTERMEDIARY: ${agentName}</span>
    </td>
  </tr></table>
</td></tr>
<tr><td style="background:#1E4276;padding:10px 20px;">
  <table width="100%"><tr>
    <td style="font-size:12px;font-weight:800;color:#fff;letter-spacing:0.4px;">STUDENTS PERSONAL ACCIDENT INVOICE</td>
    <td style="text-align:right;font-size:9px;color:rgba(255,255,255,0.75);">Date: ${dateStr}</td>
  </tr></table>
</td></tr>
<tr><td style="padding:16px 20px;">
  <p style="margin:0 0 10px;color:#1E4276;font-size:14px;">Dear ${name},</p>
  <p style="margin:0 0 14px;color:#475569;font-size:13px;line-height:1.5;">Please find your Students Personal Accident quotation below.</p>
  <table width="100%" style="margin-bottom:12px;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
    <tr style="background:#EBF5FF;"><td colspan="2" style="padding:6px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;">Bill To</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;width:35%;">Invoice No.</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${invoiceNo}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Plan</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${quote.optionName || ''}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Duration</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${durationLabel}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Quote ref</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${quoteId}</td></tr>
  </table>
  <div style="font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;margin-bottom:6px;border-bottom:2px solid #1E4276;padding-bottom:4px;">Benefits &amp; limits</div>
  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;margin-bottom:14px;">${benefitRows}</table>
  <p style="margin:0;font-size:20px;font-weight:800;color:#184679;">Total premium: KES ${formatPdfCurrency(premium)}</p>
  <p style="margin:12px 0 0;font-size:11px;color:#94A3B8;">Valid 30 days. Cover commences upon receipt of premium.</p>
</td></tr>
<tr><td style="padding:12px 20px;background:#FFFBEB;border-top:1px solid #FDE68A;font-size:10px;color:#92400e;">
  This is a computer-generated quotation and does not require a signature.
</td></tr>
</table></td></tr></table></body></html>`;
}

export async function openPaQuotePdf(quote, clientName, options = {}) {
  if (!quote) return false;
  if (!window.jspdf?.jsPDF) {
    throw new Error('PDF library not loaded');
  }
  const { jsPDF: JsPDF } = window.jspdf;
  const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  buildPaQuotePDF(doc, quote, {
    ...options,
    clientName: clientName || quote.proposerName,
  });
  const blob = doc.output('bloburl');
  window.open(blob, '_blank');
  return true;
}
